import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import { newsArticles } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const article = newsArticles.find((a) => a.id === id);
  const [copied, setCopied] = useState(false);

  useDocumentMeta(
    article ? `${article.title} - 芒果tv资讯` : "资讯未找到 - 芒果tv",
    article ? `${article.summary}（${article.category} · ${article.publishedAt}）` : undefined
  );

  const related = useMemo(() => {
    if (!article) return [];
    const same = newsArticles.filter((a) => a.id !== article.id && a.category === article.category);
    const others = newsArticles.filter((a) => a.id !== article.id && a.category !== article.category);
    return [...same, ...others].slice(0, 4);
  }, [article]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      // ignore
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  if (!article) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl" />
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">资讯不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">这篇资讯可能已被移除或链接有误。</p>
        <Link
          to="/news"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "娱乐资讯", path: "/news" }, { label: article.category }]} />

      <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
        <article className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <span className="inline-flex items-center gap-1 rounded bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-700">
            {article.category}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">
            {article.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-price-tag-3-line" />
              {article.source}
            </span>
            <button
              type="button"
              onClick={handleShare}
              className="ml-auto inline-flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line" />
              {copied ? "链接已复制" : "分享"}
            </button>
          </div>

          <div className="mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title} - 芒果tv 娱乐资讯`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="mt-6 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
            {article.content.map((p, i) => (
              <div key={i}>
                <p>{p}</p>
                {i === 1 && (
                  <figure className="my-5">
                    <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
                      <img
                        src={article.inlineImage}
                        alt={`${article.title} 相关内容配图`}
                        title={`${article.title} - 芒果tv`}
                        className="h-full w-full object-cover object-top"
                      />
                    </div>
                    <figcaption className="mt-2 text-center text-xs text-foreground-400">
                      配图 · 与本文内容相关的示意图
                    </figcaption>
                  </figure>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200 pt-6">
            {article.tags.map((t) => (
              <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-700">
                #{t}
              </span>
            ))}
          </div>
        </article>

        <aside>
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-link text-primary-600" />
              相关文章
            </h2>
            <div className="mt-4 flex flex-col gap-3">
              {related.map((a) => (
                <NewsCard key={a.id} article={a} />
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}